import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { LogService } from 'src/app/@core';
import { FacilitiesApiService } from 'src/app/@core/api/facilities-api.service';
import { Facility } from 'src/app/@core/domain/interfaces/facility.interface';
import { ParkbaseStorageService } from 'src/app/@shared/services/parkbaseStorage.service';
import { TrackingService } from 'src/app/@shared/services/tracking.service';

@Component({
  selector: 'app-facility-select',
  templateUrl: './facility-select.component.html',
  styleUrls: ['./facility-select.component.scss'],
})
export class FacilitySelectComponent implements OnInit {
  facilities$!: Observable<Facility[]>;
  facilitiesForm!: UntypedFormGroup;
  currentFacility!: Facility;
  optionAll = { id: 0, name: '', availableActions: [] };

  constructor(
    private facilitiesApiService: FacilitiesApiService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private routes: ActivatedRoute,
    private translate: TranslateService,
    private parkbaseStorageService: ParkbaseStorageService,
    private trackingService: TrackingService,
    private logger: LogService
  ) {}

  get facility(): AbstractControl | null {
    return this.facilitiesForm.get('facilityName');
  }

  ngOnInit(): void {
    this.facilities$ = this.facilitiesApiService.facilities;

    this.facilitiesForm = this.formBuilder.group({
      facilityName: [this.currentFacility],
    });

    this.routes.params.subscribe((params: Params) => {
      if (params && params['id']) {
        this.currentFacility = { id: params['id'], name: '', availableActions: [] };
      }
    });

    if (this.currentFacility === undefined) {
      this.currentFacility = this.optionAll;
    }

    this.translate.get('FACILITY_SELECT.OPTION_ALL').subscribe((res: string) => {
      this.optionAll.name = res;
    });

    const facilities = this.parkbaseStorageService.retrieveFacilities();
    if (facilities && facilities.length > 0) {
      const foundFacility = facilities.find((f: Facility) => {
        return Number(f.id) === Number(this.currentFacility.id);
      });
      if (foundFacility) {
        this.parkbaseStorageService.storeFacilitySelection(foundFacility);
        this.currentFacility = foundFacility;
      } else {
        this.parkbaseStorageService.storeFacilitySelection(this.optionAll);
        this.currentFacility = this.optionAll;
      }
    }
  }

  compareById(v1: Facility, v2: Facility): boolean {
    return v1.id === v2.id;
  }

  changeFacility($e: Facility): void {
    this.findFacilityAndRedirect($e);
  }

  /**
   * Redirect user to facility, works with DirectAccessGuard to prevent url navigation
   * @param facility Redirect
   */
  private findFacilityAndRedirect(facility: Facility): void {
    this.trackingService.eventEmitter(
      'navigateToFacility',
      'action',
      'select facility',
      facility.toString(),
      0
    );
    if (facility.name === this.optionAll.name) {
      this.router
        .navigateByUrl('/', { skipLocationChange: false })
        .then(() => this.router.navigate(['/status/facilities']));
    } else {
      const facilitiesFromStorage = this.parkbaseStorageService.retrieveFacilities();
      const found = facilitiesFromStorage.find((f: Facility) => {
        return String(facility.name) === String(f.name);
      });
      if (found) {
        this.logger.info('Navigated to facility detail');
        this.parkbaseStorageService.storeFacilitySelection(found);
        this.router.navigateByUrl('/', { skipLocationChange: false }).then(() =>
          this.router.navigateByUrl('/status/facilities/' + found.id, {
            skipLocationChange: true,
          })
        );
      }
    }
  }
}
